import { Briefcase, Check, ChevronDown, Star } from "lucide-react";
import { useAccounts } from "@/context/AccountContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const BROKER_BADGES = {
  ftmo: "#f59e0b",
  exness: "#a78bfa",
  icmarkets: "#3b82f6",
  pepperstone: "#10b981",
  titanfx: "#ef4444",
  generic: "#6b7280",
};

/**
 * Global account switcher — mounted in AppLayout top bar.
 *
 * Visibility rules:
 *   - Always mounted so its space in the top-bar is stable across loads.
 *   - Shows an inline skeleton while AccountContext is loading.
 *   - Shows a static (non-clickable) pill when the user has exactly one account
 *     so people still see WHICH account they're on.
 *   - Shows the full dropdown when they have 2+ accounts.
 */
export default function AccountPicker({ compact = false }) {
  const { accounts, activeAccount, setActiveAccountId, loading } = useAccounts();
  const activeAccounts = (accounts || []).filter((a) => a.is_active !== false);
  const active = activeAccount || activeAccounts[0] || (accounts || [])[0];
  const badge = BROKER_BADGES[active?.broker_id] || "#6b7280";
  const sizeCls = compact ? "h-8 px-2.5 text-xs" : "h-9 px-3 text-xs";

  // Loading skeleton — same footprint so the layout doesn't jump.
  if (loading && !active) {
    return (
      <div
        className={`flex items-center gap-2 rounded-full border border-[#1f2937] bg-[#111827] ${sizeCls}`}
        data-testid="account-picker-loading"
      >
        <div className="w-2 h-2 rounded-full bg-[#374151] animate-pulse" />
        <div className="w-24 h-3 rounded bg-[#1f2937] animate-pulse" />
      </div>
    );
  }

  // No accounts yet (should not happen after migration) — bare shell.
  if (!active) {
    return (
      <div
        className={`flex items-center gap-2 rounded-full border border-[#1f2937] bg-[#111827] text-[#6b7280] ${sizeCls}`}
        data-testid="account-picker-empty"
      >
        <Briefcase className="w-3 h-3" />
        <span className="font-mono">No account</span>
      </div>
    );
  }

  // Single-account: static, non-clickable pill.
  if (activeAccounts.length <= 1) {
    return (
      <div
        className={`flex items-center gap-2 rounded-full border border-[#1f2937] bg-[#111827] ${sizeCls}`}
        data-testid="account-picker-single"
        title={`${active.account_name} · ${active.broker_id}`}
      >
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: badge }} />
        <Briefcase className="w-3 h-3 text-[#9ca3af]" />
        <span className="text-[10px] uppercase tracking-widest text-[#6b7280] font-mono">Account:</span>
        <span className="font-mono font-semibold max-w-[180px] truncate text-[#e5e7eb]">
          {active.account_name}
        </span>
        {active.is_default && <Star className="w-3 h-3 text-[#10b981] fill-[#10b981]" />}
      </div>
    );
  }

  // 2+ accounts: full dropdown switcher.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-2 rounded-full border border-[#3b82f6]/40 bg-[#3b82f6]/10 hover:bg-[#3b82f6]/20 hover:border-[#3b82f6] transition-colors font-semibold ${sizeCls}`}
          data-testid="account-picker-trigger"
          title="Switch account"
        >
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: badge }} />
          <Briefcase className="w-3 h-3 text-[#3b82f6]" />
          <span className="text-[10px] uppercase tracking-widest text-[#3b82f6] font-mono">Account:</span>
          <span className="font-mono max-w-[180px] truncate text-[#e5e7eb]">
            {active.account_name}
          </span>
          {active.is_default && <Star className="w-3 h-3 text-[#10b981] fill-[#10b981]" />}
          <ChevronDown className="w-3.5 h-3.5 text-[#3b82f6]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="min-w-[280px] bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb] z-50"
        data-testid="account-picker-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#6b7280]">
          Switch account ({activeAccounts.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#1f2937]" />
        {activeAccounts.map((a) => {
          const c = BROKER_BADGES[a.broker_id] || "#6b7280";
          const isActive = a.id === active?.id;
          return (
            <DropdownMenuItem
              key={a.id}
              onClick={() => setActiveAccountId(a.id)}
              data-testid={`account-picker-item-${a.id}`}
              className="flex items-center gap-2 cursor-pointer focus:bg-[#1f2937] focus:text-white"
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {a.account_name}
                  {a.is_default && <Star className="w-3 h-3 text-[#10b981] fill-[#10b981]" />}
                </div>
                <div className="text-[10px] text-[#6b7280] font-mono">
                  {a.broker_id} · {a.currency}
                </div>
              </div>
              {isActive && <Check className="w-3.5 h-3.5 text-[#10b981]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
